import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["trigger", "content"]
  static values = {
    default: String,
    persistKey: { type: String, default: "" },
    disableHash: { type: Boolean, default: false }
  }

  connect() {
    const savedValue = this.getSavedTab()
    const defaultValue = savedValue || this.defaultValue || this.triggerTargets[0]?.dataset.value
    if (defaultValue) {
      this.activateTab(defaultValue, false) // Don't persist on initial load
    }

    // Listen for hash changes (back/forward navigation) only when not using localStorage
    if (!this.persistKeyValue && !this.disableHashValue) {
      this.hashChangeHandler = this.handleHashChange.bind(this)
      window.addEventListener('hashchange', this.hashChangeHandler)
    }
  }

  disconnect() {
    if (this.hashChangeHandler) {
      window.removeEventListener('hashchange', this.hashChangeHandler)
    }
  }

  getSavedTab() {
    const restoredTab = this.getRestoredTab()
    if (restoredTab) return restoredTab

    if (this.persistKeyValue) {
      const saved = localStorage.getItem(this.persistKeyValue)
      if (saved) {
        // Validate saved value is still a valid tab
        const validTab = this.triggerTargets.find(trigger => trigger.dataset.value === saved)
        return validTab ? saved : null
      }
      return null
    }

    // Fallback to URL hash
    if (this.disableHashValue) return null

    const hash = window.location.hash.slice(1)
    if (!hash) return null

    const validTab = this.triggerTargets.find(trigger => trigger.dataset.value === hash)
    return validTab ? hash : null
  }

  getRestoredTab() {
    if (!this.persistKeyValue) return null

    const restoreKey = `${this.persistKeyValue}-return-once`
    const restored = sessionStorage.getItem(restoreKey)
    if (!restored) return null

    sessionStorage.removeItem(restoreKey)

    const validTab = this.triggerTargets.find(trigger => trigger.dataset.value === restored)
    return validTab ? restored : null
  }

  handleHashChange() {
    const tabValue = this.getSavedTab()
    if (tabValue) {
      this.activateTab(tabValue, false)
    }
  }

  switch(event) {
    const value = event.currentTarget.dataset.value
    this.activateTab(value, true)
  }

  activateTab(value, shouldPersist = true) {
    // Persist tab state
    if (shouldPersist) {
      if (this.persistKeyValue) {
        localStorage.setItem(this.persistKeyValue, value)
      } else if (!this.disableHashValue && window.location.hash.slice(1) !== value) {
        history.replaceState(null, null, `#${value}`)
      }
    }

    // Update triggers
    this.triggerTargets.forEach(trigger => {
      const isActive = trigger.dataset.value === value
      trigger.dataset.active = isActive ? "true" : "false"

      // Add active styling for tab buttons (Card/Map style)
      if (isActive) {
        trigger.classList.add("bg-[#f2f3f5]", "text-[#1f2937]")
        trigger.classList.remove("bg-white", "text-[#6b7280]")
      } else {
        trigger.classList.remove("bg-[#f2f3f5]", "text-[#1f2937]")
        trigger.classList.add("bg-white", "text-[#6b7280]")
      }
    })

    // Update contents
    this.contentTargets.forEach(content => {
      const isActive = content.dataset.value === value

      if (isActive) {
        content.classList.remove("hidden")
      } else {
        content.classList.add("hidden")
      }
    })

    // Dispatch custom event to notify other controllers (e.g., comment controller)
    this.dispatch("tabChanged", { detail: { activeTab: value } })
  }
}
